import { useState } from "react";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import LanguagePicker from "../components/LanguagePicker";
import LearningLevelPicker from "../components/LearningLevelPicker";
import StudyNotes from "../components/StudyNotes";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import { Loader2, Award, Sprout, Flame, Crown } from "lucide-react";

const ALL_BADGES = [
  { id: "rising", points: 100, label: "Rising Talker", Icon: Sprout },
  { id: "fluent", points: 250, label: "Fluent Friend", Icon: Flame },
  { id: "master", points: 500, label: "Conversation Master", Icon: Crown },
];

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: user.name || "", username: user.username || "", bio: user.bio || "", country: user.country || "",
  });
  const [native, setNative] = useState(user.native_languages || []);
  const [learning, setLearning] = useState(user.learning_languages || []);
  const [levels, setLevels] = useState(user.learning_levels || {});
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => { const v = e.target.value; setForm((f) => ({ ...f, [k]: v })); };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // keep only levels for languages still being learned
      const cleaned = Object.fromEntries(learning.map((l) => [l, levels[l] || "Beginner"]));
      const { data } = await api.put("/profile", { ...form, native_languages: native, learning_languages: learning, learning_levels: cleaned });
      setUser(data.user);
      setLevels(data.user.learning_levels || {});
      toast.success(t("profile.saved"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const initials = (user.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-3xl bg-accent flex items-center justify-center font-heading font-black text-2xl text-primary">
            {initials}
          </div>
          <div>
            <h1 className="text-3xl font-heading font-black tracking-tight">{user.name}</h1>
            <p className="text-foreground/70">{user.email}</p>
          </div>
        </div>

        <div className="mt-8 rounded-3xl bg-card border border-border shadow-sm p-6" data-testid="achievements-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-heading font-black text-lg flex items-center gap-2"><Award className="w-5 h-5 text-secondary" /> Achievements</h2>
            <span className="text-sm text-foreground/70" data-testid="profile-points">{user.points || 0} pts · {user.tier}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
            {ALL_BADGES.map((b) => {
              const earned = (user.points || 0) >= b.points;
              return (
                <div key={b.id} data-testid={`badge-${b.id}`} className={`rounded-2xl border p-4 flex items-center gap-3 transition-all ${earned ? "border-secondary/40 bg-secondary/10" : "border-border bg-accent/30 opacity-60"}`}>
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${earned ? "bg-secondary text-secondary-foreground" : "bg-muted text-muted-foreground"}`}>
                    <b.Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-sm">{b.label}</p>
                    <p className="text-xs text-muted-foreground">{earned ? "Unlocked" : `${b.points} pts to unlock`}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <form onSubmit={save} className="mt-8 rounded-3xl bg-card border border-border shadow-sm p-8 space-y-6" data-testid="profile-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label>{t("profile.name")}</Label>
              <Input value={form.name} onChange={set("name")} className="rounded-2xl bg-accent/50 border-transparent h-11" data-testid="profile-name" />
            </div>
            <div className="space-y-2">
              <Label>Username</Label>
              <Input value={form.username} onChange={set("username")} placeholder="unique_handle" className="rounded-2xl bg-accent/50 border-transparent h-11" data-testid="profile-username" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("profile.country")}</Label>
            <Input value={form.country} onChange={set("country")} className="rounded-2xl bg-accent/50 border-transparent h-11" data-testid="profile-country" />
          </div>
          <div className="space-y-2">
            <Label>{t("profile.bio")}</Label>
            <Textarea value={form.bio} onChange={set("bio")} rows={3} placeholder={t("profile.bioPlaceholder")}
              className="rounded-2xl bg-accent/50 border-transparent" data-testid="profile-bio" />
          </div>
          <LanguagePicker label={t("profile.iSpeak")} selected={native} onChange={setNative} color="secondary" testid="profile-native" />
          <LanguagePicker label={t("profile.imLearning")} selected={learning} onChange={setLearning} color="primary" testid="profile-learning" />
          <LearningLevelPicker label={t("profile.flashcardLevel")} languages={learning} levels={levels} onChange={setLevels} testid="profile-levels" />

          <Button type="submit" disabled={loading} className="rounded-full h-11 px-8" data-testid="profile-save">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("profile.save")}
          </Button>
        </form>

        <div className="mt-10 pt-8 border-t border-border">
          <StudyNotes />
        </div>
      </div>
    </div>
  );
}
